import { Button, Dialog, DialogContent, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { useSetMallAutoBookingConfig } from "@saleor/autobooking/mutations";
import { useModalStyles } from "@saleor/autobooking/styles/modalStyles";
import {
  ExpressFleetMallsConfig_malls_edges_node,
  ExpressFleetMallsConfigVariables
} from "@saleor/autobooking/types/ExpressFleetMallsConfig";
import { SetMallAutoBookingConfig_setMallAutoBookingConfig_autoBookingError } from "@saleor/autobooking/types/SetMallAutoBookingConfig";
import {
  checkErrorForMall,
  expressFleetCourierChoices
} from "@saleor/autobooking/utils/handlers";
import {
  getUniqueStringFromArray,
  removeLastPeriod
} from "@saleor/autobooking/utils/misc";
import AutoBookingConfig, {
  AUTOBOOKING_LABELS,
  ERROR_FIELDS
} from "@saleor/autobooking/utils/objects";
import useNotifier from "@saleor/hooks/useNotifier";
import { DialogProps } from "@saleor/types";
import {
  AutobookingConfigUpdateInput,
  StoreTypeEnum
} from "@saleor/types/globalTypes";
import React, { useState } from "react";

import CircularLoading from "../CircularLoading";
import AutoBookingDialogForm from "./AutoBookingDialogForm";
import AutoBookingDialogFormHeader from "./AutoBookingDialogFormHeader";

export interface AutoBookingDialogProps extends DialogProps {
  mall: ExpressFleetMallsConfig_malls_edges_node;
  refetch?: (variables?: ExpressFleetMallsConfigVariables) => any;
  activeTab?: string;
}

const AutoBookingDialog: React.FC<AutoBookingDialogProps> = props => {
  const { onClose, open, mall, refetch, activeTab } = props;

  const classes = useModalStyles(props);
  const notify = useNotifier();

  const [isFbTab, setIsFbTab] = useState(true);
  const [fbInput, setFbInput] = useState<AutoBookingConfig>(null);
  const [gmInput, setGmInput] = useState<AutoBookingConfig>(null);
  const [fbEnabled, setFbEnabled] = useState(false);
  const [gmEnabled, setGmEnabled] = useState(false);
  const [fbDefaultValues, setFbDefaultValues] = useState({
    courier: "",
    enabled: false,
    input: null
  });
  const [gmDefaultValues, setGmDefaultValues] = useState({
    courier: "",
    enabled: false,
    input: null
  });
  const [isLoadingDialog, setIsLoadingDialog] = useState(true);
  const [fbErrors, setFbErrors] = useState([]);
  const [gmErrors, setGmErrors] = useState([]);

  const disabledField = isFbTab ? !fbEnabled : !gmEnabled;

  React.useEffect(() => {
    if (mall?.id) {
      setIsLoadingDialog(false);
    }
  }, [mall]);

  const resetSubmitResult = () => {
    setFbErrors([]);
    setGmErrors([]);
  };

  React.useEffect(() => {
    resetSubmitResult();

    if (!open) {
      setFbInput(null);
      setFbEnabled(false);

      setGmInput(null);
      setGmEnabled(false);
    } else {
      setIsFbTab(true);
      loadDefaultValues();
    }

    if (activeTab === "fnb") {
      setIsFbTab(true);
    } else {
      setIsFbTab(false);
    }
  }, [open]);

  const loadDefaultValues = () => {
    const fbStoreType = mall?.fbAutoBookingConfig?.storeType;
    const gmStoreType = mall?.gmAutoBookingConfig?.storeType;

    if (fbStoreType) {
      setFbInput(mall.fbAutoBookingConfig);
      setFbEnabled(mall.fbAutoBookingConfig.isEnabled);
      setFbDefaultValues({
        courier: mall?.fbAutoBookingConfig?.courier || "",
        enabled: mall?.fbAutoBookingConfig?.isEnabled || false,
        input: mall.fbAutoBookingConfig
      });
    }
    if (gmStoreType) {
      setGmInput(mall.gmAutoBookingConfig);
      setGmEnabled(mall.gmAutoBookingConfig.isEnabled);
      setGmDefaultValues({
        courier: mall?.gmAutoBookingConfig?.courier || "",
        enabled: mall?.gmAutoBookingConfig?.isEnabled || false,
        input: mall.gmAutoBookingConfig
      });
    }
  };

  const assignFormValues = () => {
    const config: AutobookingConfigUpdateInput = {
      anyStatusMinutes: isFbTab
        ? Number(fbInput?.anyStatusMinutes)
        : Number(gmInput?.anyStatusMinutes),
      collectedStatusMinutes: isFbTab
        ? Number(fbInput?.collectedStatusMinutes)
        : Number(gmInput?.collectedStatusMinutes),
      courier: isFbTab ? fbInput?.courier : gmInput?.courier,
      enabled: isFbTab ? fbEnabled : gmEnabled,
      readyStatusMinutes: isFbTab
        ? Number(fbInput?.readyStatusMinutes)
        : Number(gmInput?.readyStatusMinutes),
      storeType: isFbTab
        ? StoreTypeEnum.FOOD_AND_BEVERAGES
        : StoreTypeEnum.GENERAL_MERCHANDISE
    };

    const inputValues = {
      fbConfig: config,
      gmConfig: config
    };
    return inputValues;
  };

  const formValues = assignFormValues();

  const submitAutoBooking = () => {
    resetSubmitResult();
    setIsLoadingDialog(true);

    if (isFbTab) {
      saveEditAutoBooking({
        variables: {
          input: {
            ...formValues.fbConfig,
            enabled: fbEnabled
          },
          mallId: mall.id
        }
      });
    } else {
      saveEditAutoBooking({
        variables: {
          input: {
            ...formValues.gmConfig,
            enabled: gmEnabled
          },
          mallId: mall.id
        }
      });
    }
  };

  const [saveEditAutoBooking] = useSetMallAutoBookingConfig({
    onCompleted: data => {
      const autoBookingError =
        data?.setMallAutoBookingConfig?.autoBookingError || [];
      const newErrors = [
        {
          errors: autoBookingError,
          tab: isFbTab
            ? AUTOBOOKING_LABELS.SHORT_FNB
            : AUTOBOOKING_LABELS.SHORT_GM
        }
      ];

      if (autoBookingError.length === 0) {
        handleSaved([]);
      } else {
        if (isFbTab) {
          setFieldErrors(autoBookingError, setFbErrors);
        } else {
          setFieldErrors(autoBookingError, setGmErrors);
        }
        handleSaved(newErrors);
      }
    }
  });

  const getToastErrors = (
    _errors: Array<{
      errors: SetMallAutoBookingConfig_setMallAutoBookingConfig_autoBookingError[];
      tab: string;
    }>
  ) => {
    const errorList: string[] = [];
    const tabErrorList: string[] = [];
    const errorMessageContainer: Array<{
      original: string;
      updated: string;
    }> = [];

    _errors.forEach(error => {
      error.errors.forEach(
        (
          e: SetMallAutoBookingConfig_setMallAutoBookingConfig_autoBookingError
        ) => {
          if (checkErrorForMall(e.field)) {
            errorList.push(e.message);
            tabErrorList.push(error.tab);
          } else {
            const formattedMessage = `${removeLastPeriod(e.message)} for ${
              error.tab
            }`;
            const existingError = errorMessageContainer.find(
              m => m.original === e.message
            );

            if (!existingError) {
              errorMessageContainer.push({
                original: e.message,
                updated: formattedMessage
              });
              errorList.push(formattedMessage);
              tabErrorList.push(error.tab);
            }
          }
        }
      );
    });

    return {
      errorMessages: errorList,
      tabWithError: tabErrorList
    };
  };

  const handleSaved = (errors: any) => {
    const consolidatedErrors = getToastErrors(errors);
    const errorMessages = consolidatedErrors.errorMessages;
    const tabWithError = consolidatedErrors.tabWithError;
    const uniqueTabWithError = getUniqueStringFromArray(tabWithError);
    const uniqueMessages = getUniqueStringFromArray(errorMessages);

    if (uniqueTabWithError.length === 0) {
      const successTab = isFbTab
        ? AUTOBOOKING_LABELS.FNB
        : AUTOBOOKING_LABELS.GM;
      promptSuccess(successTab);
    } else {
      promptError(uniqueMessages, uniqueTabWithError);
    }
    setIsLoadingDialog(false);
  };

  const promptError = (uniqueMessages, uniqueTabWithError) => {
    uniqueMessages.forEach(error => {
      notify({
        status: "error",
        text: error
      });
    });

    if (uniqueTabWithError[0] === AUTOBOOKING_LABELS.SHORT_FNB) {
      if (!isFbTab) {
        setIsFbTab(true);
      }
    } else if (uniqueTabWithError[0] === AUTOBOOKING_LABELS.SHORT_GM) {
      if (isFbTab) {
        setIsFbTab(false);
      }
    }
  };

  const promptSuccess = (tab?: string) => {
    const courier = isFbTab
      ? formValues.fbConfig?.courier
      : formValues.gmConfig?.courier;
    const matchedCourier = expressFleetCourierChoices.find(
      choice => choice.value === courier
    );
    const courierLabel = matchedCourier?.label || courier;

    const defaultInput = isFbTab
      ? fbDefaultValues.input
      : gmDefaultValues.input;
    const input = isFbTab ? formValues.fbConfig : formValues.gmConfig;

    const courierChanged = input.courier !== defaultInput.courier;
    const enabledChanged =
      (input.enabled ?? input.enabled) !== defaultInput.enabled;
    const readyStatusChanged =
      input.readyStatusMinutes !== defaultInput.readyStatusMinutes;
    const collectedStatusChanged =
      input.collectedStatusMinutes !== defaultInput.collectedStatusMinutes;
    const anyStatusChanged =
      input.anyStatusMinutes !== defaultInput.anyStatusMinutes;

    const timingChanged =
      readyStatusChanged || collectedStatusChanged || anyStatusChanged;

    if (
      (courierChanged && !enabledChanged && !timingChanged) ||
      (courierChanged && enabledChanged && !timingChanged)
    ) {
      notify({
        status: "success",
        text: `${courierLabel} has been successfully assigned to ${mall?.name}`
      });
    } else {
      notify({
        status: "success",
        text: "Changes saved for " + tab + "!"
      });
    }
    onClose();
    refetch();
  };

  const setFieldErrors = (
    autoBookingError: any[],
    setErrorState: (value: React.SetStateAction<any[]>) => void
  ) => {
    let errors = autoBookingError;

    const getWithInputErrors = autoBookingError
      ?.filter(e => e.field === ERROR_FIELDS.INPUT)
      ?.map(e => ({ ...e, field: ERROR_FIELDS.READY }));
    if (getWithInputErrors.length > 0) {
      errors = [...errors, ...getWithInputErrors];
      const fbErrorInput = getWithInputErrors.map(e => ({
        ...e,
        field: ERROR_FIELDS.COLLECTED
      }));
      errors = [...errors, ...fbErrorInput];
    }

    setErrorState(errors);
  };

  const resetFieldErrors = (
    errorState: any[],
    setErrorState: (value: React.SetStateAction<any[]>) => void,
    field?: string
  ) => {
    let errors = [];

    if (field) {
      errors = errorState?.filter(e => e.field !== field);
    }

    setErrorState(errors);
  };

  const isInputValid = (input: any, defaultInput: any) => {
    const formInput = input?.input;
    if (!formInput) {
      return false;
    }
    if (
      input.courier === undefined ||
      formInput.readyStatusMinutes === undefined ||
      formInput.collectedStatusMinutes === undefined ||
      formInput.anyStatusMinutes === undefined
    ) {
      return false;
    }

    const defaultFormInput = defaultInput?.input;
    if (!defaultInput || !defaultFormInput) {
      return true;
    }

    const isChanged =
      input.courier !== defaultInput.courier ||
      input.enabled !== defaultInput.enabled ||
      formInput.readyStatusMinutes !== defaultFormInput.readyStatusMinutes ||
      formInput.collectedStatusMinutes !==
        defaultFormInput.collectedStatusMinutes ||
      formInput.anyStatusMinutes !== defaultFormInput.anyStatusMinutes;

    return isChanged;
  };

  const isCurrentInputValid = isFbTab
    ? isInputValid(
        {
          courier: fbInput?.courier,
          enabled: fbEnabled,
          input: fbInput
        },
        fbDefaultValues
      )
    : isInputValid(
        {
          courier: gmInput?.courier,
          enabled: gmEnabled,
          input: gmInput
        },
        gmDefaultValues
      );

  return (
    <Dialog
      onClose={onClose}
      open={open}
      maxWidth={false}
      fullWidth={false}
      PaperProps={{
        style: {
          borderRadius: "15px",
          width: "500px"
        }
      }}
    >
      <DialogContent className={classes.modal}>
        <div className={classes.modalHeader}>
          <IconButton
            aria-label="close"
            onClick={() => onClose()}
            className={classes.modalCloseBtn}
          >
            <CloseIcon />
          </IconButton>
        </div>

        <div className={classes.modalCenter}>
          <div className={classes.modalForm}>
            <AutoBookingDialogFormHeader
              mallName={mall?.name}
              isFbTab={isFbTab}
              setIsFbTab={setIsFbTab}
            />

            {isLoadingDialog ? (
              <div
                className={`${classes.modalFormBody} ${classes.boxStylesModalLoading}`}
              >
                <CircularLoading />
              </div>
            ) : (
              <div className={classes.modalFormBody}>
                {isFbTab ? (
                  <AutoBookingDialogForm
                    storeType={AUTOBOOKING_LABELS.FNB}
                    disabledField={disabledField}
                    enabled={fbEnabled}
                    setEnabled={setFbEnabled}
                    config={fbInput}
                    setConfig={setFbInput}
                    errors={fbErrors}
                    resetFormErrors={(field?: string) => {
                      resetFieldErrors(fbErrors, setFbErrors, field);
                    }}
                    courierChoices={expressFleetCourierChoices}
                  />
                ) : (
                  <AutoBookingDialogForm
                    storeType={AUTOBOOKING_LABELS.GM}
                    disabledField={disabledField}
                    enabled={gmEnabled}
                    setEnabled={setGmEnabled}
                    config={gmInput}
                    setConfig={setGmInput}
                    errors={gmErrors}
                    resetFormErrors={(field?: string) => {
                      resetFieldErrors(gmErrors, setGmErrors, field);
                    }}
                    courierChoices={expressFleetCourierChoices}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        <div
          className={classes.modalBottom}
          style={{ padding: "0px 33px 0px 30px" }}
        >
          <Button
            id="cancelDialogActionBtn"
            autoFocus
            onClick={onClose}
            color="primary"
            style={{
              backgroundColor: "#F1F2F3",
              marginRight: 10
            }}
            className={classes.btnCancel}
            disabled={isLoadingDialog}
          >
            <span style={{ color: "#747C8C" }}>Cancel</span>
          </Button>
          <Button
            id="confirmDialogActionBtn"
            autoFocus
            onClick={submitAutoBooking}
            color="primary"
            variant="contained"
            className={classes.btnYes}
            disabled={isLoadingDialog || !isCurrentInputValid}
          >
            <span className={classes.txtWhite}>SAVE</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

AutoBookingDialog.displayName = "AutoBookingDialog";
export default AutoBookingDialog;
